import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, XCircle, Loader, X } from 'lucide-react';
import { scoringCategories } from '../data/scoringCategories';
import { searchClient } from '../utils/algolia';
import { extractDataFromPDF } from '../utils/gemini';

const DocumentUploadView = () => {
  const [file, setFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [totalScore, setTotalScore] = useState(null);
  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [showMatches, setShowMatches] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Calculate score from extracted data
  const calculateScore = (data) => {
    const scores = {};

    // Map extracted data to scoring system
    Object.entries(data).forEach(([key, value]) => {
      if (value) {
        scores[key] = value;
      }
    });

    // Calculate category scores
    const calculateCategoryScore = (category) => {
      const categoryData = scoringCategories[category];
      let totalPoints = 0;
      let maxPoints = 0;

      categoryData.attributes.forEach(attr => {
        const selectedValue = scores[attr.name];
        if (selectedValue !== undefined) {
          totalPoints += attr.points[selectedValue] || 0;
        }
        maxPoints += Math.max(...Object.values(attr.points));
      });

      const percentage = maxPoints > 0 ? (totalPoints / maxPoints) : 0;
      return Math.round(percentage * categoryData.weight);
    };

    // Calculate total score
    const total = Object.keys(scoringCategories).reduce((sum, category) => {
      return sum + calculateCategoryScore(category);
    }, 0);

    return { scores, total };
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (uploadedFile.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }

    setFile(uploadedFile);
    setError(null);
    setIsProcessing(true);
    setExtractedData(null);
    setTotalScore(null);
    setMatchingCompanies([]);
    setShowMatches(false);

    // Process file with Gemini AI
    extractDataFromPDF(uploadedFile)
      .then((extracted) => {
        // Filter out null values for display
        const filteredData = Object.fromEntries(
          Object.entries(extracted).filter(([_, value]) => value !== null)
        );
        setExtractedData(filteredData);

        // Calculate score
        const { scores, total } = calculateScore(filteredData);
        setTotalScore({ scores, total });

        // Search Algolia for similar companies
        return searchAlgoliaForSimilarCompanies(filteredData);
      })
      .catch((err) => {
        console.error('Error processing PDF with Gemini:', err);
        setError(err.message || 'Error processing PDF. Please make sure the file is valid and Gemini API key is configured.');
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  // Map extracted data attributes to Algolia index attributes
  const mapAttributeToAlgolia = (attrName, value) => {
    const mapping = {
      companySize: 'company_size',
      revenue: 'revenue',
      timeline: 'timeline',
      budget: 'budget',
      demoRequest: 'demo_request',
    };
    
    if (!mapping[attrName]) {
      return null;
    }
    
    const algoliaField = mapping[attrName];
    let normalizedValue = value;
    
    // Normalize values to match Algolia data format
    if (attrName === 'demoRequest') {
      normalizedValue = value === 'Yes' ? 'Yes' : 'No';
    }
    
    return { [algoliaField]: normalizedValue };
  };

  // Search Algolia for similar companies based on extracted data
  const searchAlgoliaForSimilarCompanies = async (data) => {
    if (!data || Object.keys(data).length === 0) {
      setMatchingCompanies([]);
      setShowMatches(false);
      return;
    }

    setIsSearching(true);
    try {
      const filters = [];
      
      // Build filters from extracted data
      Object.entries(data).forEach(([attrName, value]) => {
        if (value) {
          const algoliaFilter = mapAttributeToAlgolia(attrName, value);
          if (algoliaFilter) {
            const [key, val] = Object.entries(algoliaFilter)[0];
            const filterValue = String(val).trim();
            filters.push(`${key}:"${filterValue}"`);
          }
        }
      });

      if (filters.length === 0) {
        setMatchingCompanies([]);
        setShowMatches(false);
        setIsSearching(false);
        return;
      }

      const filterString = filters.join(' AND ');
      
      // Try using facetFilters
      const facetFilters = filters.map(filter => {
        const match = filter.match(/(\w+):"(.+)"/);
        if (match) {
          return `${match[1]}:${match[2]}`;
        }
        return filter;
      });
      
      // First try with regular filters
      let searchResponse = await searchClient.search([
        {
          indexName: 'leads',
          query: '',
          params: {
            filters: filterString,
            hitsPerPage: 10,
            attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
          },
        },
      ]);
      
      // If no results, try with facetFilters
      if (!searchResponse?.results?.[0]?.hits || searchResponse.results[0].hits.length === 0) {
        searchResponse = await searchClient.search([
          {
            indexName: 'leads',
            query: '',
            params: {
              facetFilters: [facetFilters],
              hitsPerPage: 10,
              attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
            },
          },
        ]);
      }
      
      // If still no results, try client-side filtering
      if (!searchResponse?.results?.[0]?.hits || searchResponse.results[0].hits.length === 0) {
        const allResultsResponse = await searchClient.search([
          {
            indexName: 'leads',
            query: '',
            params: {
              hitsPerPage: 1000,
              attributesToRetrieve: ['company_name', 'total_score', 'company_size', 'revenue', 'timeline', 'budget', 'lead_tier', 'demo_request'],
            },
          },
        ]);
        
        const allHits = allResultsResponse?.results?.[0]?.hits || [];
        
        // Filter client-side
        const filteredHits = allHits.filter(hit => {
          return filters.every(filter => {
            const match = filter.match(/(\w+):"(.+)"/);
            if (match) {
              const field = match[1];
              const value = match[2];
              const hitValue = hit[field];
              return hitValue === value;
            }
            return false;
          });
        }).map(hit => ({
          ...hit,
          company_name: hit.company_name || hit.companyName || hit.name || 'Unknown Company',
          total_score: hit.total_score || hit.totalScore || 0,
          lead_tier: hit.lead_tier || hit.leadTier || null,
        }));
        
        if (filteredHits.length > 0) {
          setMatchingCompanies(filteredHits.slice(0, 10));
          setShowMatches(true);
          setIsSearching(false);
          return;
        }
      }

      const hits = searchResponse?.results?.[0]?.hits || [];
      
      if (hits.length > 0) {
        const formattedHits = hits.map(hit => ({
          ...hit,
          company_name: hit.company_name || hit.companyName || hit.name || 'Unknown Company',
          total_score: hit.total_score || hit.totalScore || 0,
          lead_tier: hit.lead_tier || hit.leadTier || null,
        }));
        setMatchingCompanies(formattedHits);
        setShowMatches(true);
      } else {
        setMatchingCompanies([]);
        setShowMatches(false);
      }
    } catch (error) {
      console.error('Error searching Algolia:', error);
      setMatchingCompanies([]);
      setShowMatches(false);
    } finally {
      setIsSearching(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "text-green-600 bg-green-50";
    if (score >= 50) return "text-yellow-600 bg-yellow-50";
    if (score >= 25) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  const getScoreLabel = (score) => {
    if (score >= 75) return "Hot Lead";
    if (score >= 50) return "Warm Lead";
    if (score >= 25) return "Cold Lead";
    return "Unqualified";
  };

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Matching Companies Panel */}
      {showMatches && (
        <div className="fixed top-20 right-4 w-80 md:w-96 max-h-[calc(100vh-6rem)] bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-blue-50">
            <h3 className="text-lg font-semibold text-gray-900">
              Similar Companies ({matchingCompanies.length})
            </h3>
            <button
              onClick={() => setShowMatches(false)}
              className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 p-4">
            {isSearching ? (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-pulse">Searching...</div>
              </div>
            ) : matchingCompanies.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No similar companies found</p>
                <p className="text-xs text-gray-400 mt-2">Try uploading a different document</p>
              </div>
            ) : (
              <div className="space-y-3">
                {matchingCompanies.map((company, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 text-sm">
                        {company.company_name || 'Unknown Company'}
                      </h4>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        company.total_score >= 75 ? 'bg-green-100 text-green-800' :
                        company.total_score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        company.total_score >= 25 ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {company.total_score}
                      </div>
                    </div>
                    {company.lead_tier && (
                      <div className="text-xs text-gray-600">{company.lead_tier}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Document Analysis & Lead Scoring</h1>
              <p className="mt-2 text-gray-600">Upload a financial document to extract data and calculate lead score</p>
            </div>
            {extractedData && matchingCompanies.length > 0 && (
              <button
                onClick={() => setShowMatches(!showMatches)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <span>Similar Companies</span>
                {isSearching ? (
                  <span className="bg-blue-500 px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                    ...
                  </span>
                ) : (
                  <span className="bg-blue-500 px-2 py-1 rounded-full text-xs font-bold">
                    {matchingCompanies.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* File Upload Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                {isProcessing ? (
                  <>
                    <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                    <p className="mb-2 text-sm text-gray-500">Processing document...</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-gray-400 mb-4" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PDF only</p>
                  </>
                )}
              </div>
              <input
                type="file"
                className="hidden"
                accept=".pdf"
                onChange={handleFileUpload}
                disabled={isProcessing}
              />
            </label>
          </div>
          {file && !isProcessing && (
            <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
              <FileText className="w-4 h-4" />
              <span>{file.name}</span>
            </div>
          )}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Extracted Data and Score Display */}
        {extractedData && totalScore && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Score Display */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow p-6 sticky top-4">
                <h2 className="text-xl font-semibold mb-4">Lead Score</h2>
                <div className="text-center">
                  <div className={`text-6xl font-bold mb-2 ${getScoreColor(totalScore.total).split(' ')[0]}`}>
                    {totalScore.total}
                  </div>
                  <div className={`text-lg font-medium mb-4 px-4 py-2 rounded-lg ${getScoreColor(totalScore.total)}`}>
                    {getScoreLabel(totalScore.total)}
                  </div>
                  <div className="text-sm text-gray-500">out of 100 points</div>
                </div>

                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-4">Category Scores</h3>
                  <div className="space-y-3">
                    {Object.entries(scoringCategories).map(([key, category]) => {
                      const categoryData = scoringCategories[key];
                      let categoryPoints = 0;
                      let maxPoints = 0;

                      categoryData.attributes.forEach(attr => {
                        const selectedValue = totalScore.scores[attr.name];
                        if (selectedValue !== undefined) {
                          categoryPoints += attr.points[selectedValue] || 0;
                        }
                        maxPoints += Math.max(...Object.values(attr.points));
                      });

                      const percentage = maxPoints > 0 ? (categoryPoints / maxPoints) : 0;
                      const categoryScore = Math.round(percentage * categoryData.weight);
                      const Icon = category.icon;

                      return (
                        <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-gray-600" />
                            <span className="font-medium text-sm">{category.title}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-bold">{categoryScore}</div>
                            <div className="text-xs text-gray-500">of {categoryData.weight}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Extracted Data Display */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Extracted Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(extractedData).map(([key, value]) => {
                    if (!value) return null;
                    
                    // Format key for display
                    const displayKey = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())
                      .trim();
                    
                    return (
                      <div key={key} className="p-4 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-700 mb-1">{displayKey}</div>
                        <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          {value}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {Object.values(extractedData).filter(v => v).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <p>No data could be extracted from the document.</p>
                    <p className="text-sm mt-2">Please ensure the document contains the required financial information.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default DocumentUploadView;
